import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './utils';

export type ListItemVariant = 'plain' | 'card';

export interface ListItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual variant.
   * - `plain`: dense full-width row styled as a dropdown list item
   *   (hover: neutral background, active: accent tint).
   * - `card`: full-width card row with border, rounded corners, and
   *   hover shadow — used for multi-line search/result cards.
   */
  variant?: ListItemVariant;
  /** Active/selected state — used for keyboard-highlighted dropdown rows. */
  active?: boolean;
  children: ReactNode;
}

const baseStyles =
  'w-full text-left transition-colors focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed';

const variantStyles: Record<ListItemVariant, { idle: string; active: string }> = {
  plain: {
    idle: 'px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0',
    active:
      'px-3 py-2 text-sm bg-accent/10 text-primary font-medium border-b border-neutral-100 last:border-b-0',
  },
  card: {
    idle: 'p-4 rounded-lg border bg-white border-neutral-200 hover:border-accent/50 hover:shadow-sm transition-all',
    active:
      'p-4 rounded-lg border bg-accent/5 border-accent shadow-sm transition-all',
  },
};

/**
 * Pressable list row — the standard primitive for dropdown results,
 * autocomplete rows, and selectable card rows.
 *
 * Use `variant="plain"` for dense dropdown lists (autocomplete, search
 * results embedded in a dropdown) and `variant="card"` for expanded result
 * rows that stand alone with a border + hover shadow.
 *
 * For single-choice "pick one" card groups, prefer `SelectableCard`
 * (border-2, accent-tinted active state).
 */
export const ListItem = forwardRef<HTMLButtonElement, ListItemProps>(
  function ListItem(props, ref) {
    const {
      variant = 'plain',
      active = false,
      type = 'button',
      className = '',
      children,
      ...rest
    } = props;

    const variantCls = active ? variantStyles[variant].active : variantStyles[variant].idle;
    const cls = cn(baseStyles, variantCls, className);

    return (
      <button ref={ref} type={type} className={cls} {...rest}>
        {children}
      </button>
    );
  },
);
